/**
 * Utility functions for number formatting
 */

/**
 * Formats a number with thousand separators using toLocaleString()
 * @param value - The value to format (can be string, number, null, or undefined)
 * @param fallback - The fallback value to display for null/undefined/empty values (default: '0')
 * @returns Formatted number string with thousand separators or fallback value
 */
export const formatNumberWithSeparators = (
  value: string | number | null | undefined, 
  fallback: string = '0'
): string => {
  // Handle null, undefined, or empty string cases
  if (value === null || value === undefined || value === '') {
    return fallback;
  }

  // If it's already a number, use it directly
  if (typeof value === 'number') {
    return value.toLocaleString();
  }

  // If it's a string, parse it
  if (typeof value === 'string') {
    // Remove any existing formatting (commas, spaces) and non-numeric characters except decimal point
    const cleanValue = value.replace(/[^0-9.-]/g, '');
    
    // If empty after cleaning, return fallback
    if (!cleanValue) {
      return fallback;
    }

    // Parse to number
    const parsed = parseFloat(cleanValue);
    
    // If parsing fails (NaN), return fallback
    if (isNaN(parsed)) {
      return fallback;
    }

    // Format with thousand separators
    return parsed.toLocaleString();
  }

  // For any other type, return fallback
  return fallback;
};

/**
 * Formats a number specifically for cases values (removes percentage indicators)
 * @param value - The value to format
 * @param fallback - The fallback value to display for null/undefined/empty values
 * @returns Formatted number string or original string if it contains percentage/status info
 */
export const formatCasesValue = (
  value: string | number | null | undefined, 
  fallback: string = '--'
): string => {
  // Handle null, undefined, or empty string cases
  if (value === null || value === undefined || value === '') {
    return fallback;
  }

  // If it's a number, format it
  if (typeof value === 'number') {
    return value.toLocaleString();
  }

  // If it's a string that contains percentage or status info (like "10,000 (-20%)"), return as-is
  if (typeof value === 'string' && (value.includes('%') || value.includes('(') || value.includes(')'))) {
    return value;
  }

  // Otherwise, use the standard number formatting
  return formatNumberWithSeparators(value, fallback);
};

/**
 * Formats a number specifically for volume values (handles both percentages and volume numbers)
 * @param value - The value to format
 * @param displayAsPercentage - Whether this is a percentage field
 * @param fallback - The fallback value to display for null/undefined/empty values
 * @returns Formatted number string or original string for percentages
 */
export const formatVolumeDisplayValue = (
  value: string | number | null | undefined, 
  displayAsPercentage: boolean = true,
  fallback: string = '0'
): string => {
  // Handle null, undefined, or empty string cases
  if (value === null || value === undefined || value === '') {
    return fallback;
  }

  // If displaying as percentage, keep original logic for percentage handling
  if (displayAsPercentage) {
    if (typeof value === 'string' && value.includes('%')) {
      return value; // Return as-is for percentage display
    }
    // Remove percentage symbol if present for non-percentage display
    return typeof value === 'string' ? value.replace('%', '') : String(value);
  }

  // For volume display (non-percentage), apply thousand separators
  return formatNumberWithSeparators(value, fallback);
};